'use client';

import React from 'react';
import { Analytics } from '@vercel/analytics/react';
import { usePathname, useRouter } from 'next/navigation';
import { Header, Footer, Main, SearchToggle } from '@layout';
import { searchSuggestBus } from '@ui';
import { t as translate } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';
import { setClientCookie } from '@/utils/setClientCookie';
import { DesignSystemNav } from '@components/design-system';
import type { HeaderNavSection, FooterLinkSection } from '@layout';

function getAdditionalMainClass(path: string) {
  const designSystemPattern = /^\/(?:[a-z]{2}\/)??design-system(\/|$)/;
  return designSystemPattern.test(path) ? 'design-system-main' : undefined;
}

const SITE_NAME_KEY = 'site_name';

/** Non-empty `site_name` from global locale, or `undefined` when the key is absent (`translate` returns the key). */
function resolveSiteNameLabel(
  locale: 'en' | 'es',
  t: typeof translate,
): string | undefined {
  const raw = t(SITE_NAME_KEY, 'global', locale);
  const rendered = renderTranslation(raw);
  if (rendered === null || rendered === '') return undefined;
  const text = typeof rendered === 'string' ? rendered : String(rendered);
  if (!text.trim()) return undefined;
  // Missing path: `t` falls back to the key string.
  if (raw === SITE_NAME_KEY) return undefined;
  return text;
}

export default function RootTemplate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const theme: 'light' | 'dark' = 'light';
  const locale: 'en' | 'es' = (pathname || '/').startsWith('/es') ? 'es' : 'en';

  const isSpanish = locale === 'es';
  const isDesignSystemRoute = !!getAdditionalMainClass(pathname || '');
  const designSystemSearchLabel = isDesignSystemRoute
    ? (() => {
        const raw = translate('ui_kit_nav.search_button', 'design-system', locale);
        return typeof raw === 'string' && raw !== 'ui_kit_nav.search_button'
          ? raw
          : isSpanish
            ? 'Abrir búsqueda'
            : 'Open search';
      })()
    : undefined;
  const appendNav = isDesignSystemRoute ? (
    <DesignSystemNav
      sectionID='design_system_nav'
      classes='design-system-nav'
      isSpanish={isSpanish}
      ctaSlot={
        <SearchToggle
          onOpen={() => searchSuggestBus.open()}
          ariaLabel={designSystemSearchLabel}
        />
      }
    />
  ) : undefined;

  const resolvePrefixedHref = (href: string, lang: 'en' | 'es'): string => {
    if (href.startsWith('/') && lang === 'es' && !href.startsWith('/es')) {
      return '/es' + (href === '/' ? '' : href);
    }
    return href;
  };

  const siteNameLabel = resolveSiteNameLabel(locale, translate);
  const siteOwnerLabel = String(renderTranslation(translate('site_owner', 'global', locale)) || '');
  const copyrightLabel = String(
    renderTranslation(translate('copyright', 'global', locale)) || ''
  );
  const footerBlurb = String(
    renderTranslation(translate('footer.footer_blurb', 'global', locale)) || ''
  );

  const headerLinksRaw = translate('header.header_links', 'global', locale) as unknown;
  const navSections: HeaderNavSection[] = Array.isArray(headerLinksRaw)
    ? headerLinksRaw
        .map((section) => {
          if (!section || typeof section !== 'object') return undefined;
          const s = section as { section_title?: unknown; links?: unknown; isDropdown?: unknown };
          const sectionTitle =
            typeof s.section_title === 'string'
              ? String(renderTranslation(s.section_title) || s.section_title)
              : '';
          const links = Array.isArray(s.links)
            ? s.links
                .map((link) => {
                  if (!link || typeof link !== 'object') return undefined;
                  const l = link as { title?: unknown; url?: unknown };
                  if (typeof l.url !== 'string') return undefined;
                  const item: HeaderNavSection['links'][number] = { url: l.url };
                  if (typeof l.title === 'string') {
                    item.text = String(renderTranslation(l.title) || l.title);
                  }
                  return item;
                })
                .filter((v): v is HeaderNavSection['links'][number] => Boolean(v))
            : [];
          const isDropdown = Boolean(s.isDropdown);
          if (!sectionTitle || links.length === 0) return undefined;
          return { section_title: sectionTitle, links, isDropdown };
        })
        .filter((v): v is HeaderNavSection => Boolean(v))
    : [];

  const footerLinksRaw = translate('footer.footer_links', 'global', locale) as unknown;
  const footerSections: FooterLinkSection[] = Array.isArray(footerLinksRaw)
    ? footerLinksRaw
        .map((section) => {
          if (!section || typeof section !== 'object') return undefined;
          const s = section as { section_title?: unknown; links?: unknown };
          const sectionTitle =
            typeof s.section_title === 'string'
              ? String(renderTranslation(s.section_title) || s.section_title)
              : '';
          const links = Array.isArray(s.links)
            ? s.links
                .map((link) => {
                  if (!link || typeof link !== 'object') return undefined;
                  const l = link as { title?: unknown; url?: unknown; target?: unknown };
                  if (typeof l.title !== 'string' || typeof l.url !== 'string') return undefined;
                  return {
                    title: String(renderTranslation(l.title) || l.title),
                    url: l.url,
                    target: typeof l.target === 'string' ? l.target : '_self',
                  };
                })
                .filter((v): v is { title: string; url: string; target: string } => Boolean(v))
            : [];
          if (!sectionTitle || links.length === 0) return undefined;
          return { section_title: sectionTitle, links };
        })
        .filter((v): v is FooterLinkSection => Boolean(v))
    : [];

  const handleLanguageToggle = () => {
    const cleanPath = (pathname || '/').split('#')[0];
    const scrollToTop = () => {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
    };
    if (isSpanish) {
      const newPath = cleanPath.replace(/^\/es/, '') || '/';
      setClientCookie('locale', 'en');
      router.replace(newPath, { scroll: false });
    } else {
      setClientCookie('locale', 'es');
      router.replace('/es' + (cleanPath === '/' ? '' : cleanPath), { scroll: false });
    }
    setTimeout(() => scrollToTop(), 50);
    setTimeout(() => scrollToTop(), 200);
    setTimeout(() => scrollToTop(), 400);
  };

  const handleResolvedThemeChange = (resolvedTheme: 'light' | 'dark') => {
    setClientCookie('theme', resolvedTheme);
  };

  return (
    <>
      <Header
        initialTheme={theme}
        appendNav={appendNav}
        locale={locale}
        headerHref={locale === 'es' ? '/es' : '/'}
        siteNameLabel={siteNameLabel}
        siteOwnerLabel={siteNameLabel ? undefined : siteOwnerLabel}
        currentPath={pathname || '/'}
        navSections={navSections}
        resolveNavHref={resolvePrefixedHref}
        showLanguageToggle
        onToggleLanguage={handleLanguageToggle}
        onResolvedThemeChange={handleResolvedThemeChange}
      />
      <Main
        mainID='main-content'
        className={getAdditionalMainClass(pathname || '') ?? ''}
      >
        {children}
      </Main>
      <Footer
        locale={locale}
        siteNameLabel={siteNameLabel}
        siteOwnerLabel={siteOwnerLabel}
        copyrightLabel={copyrightLabel}
        footerBlurb={footerBlurb}
        linkSections={footerSections}
        resolveInternalHref={resolvePrefixedHref}
      />
      <Analytics />
    </>
  );
}
